import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import { toText } from '../lib/toText';
import type {
  DepositApproveResult,
  DepositListResponse,
  DepositProposePayload,
  DepositRequest,
  DepositStatus,
} from '../types/finance';

interface ListFilters {
  status?: DepositStatus[];
  user_id?: string;
  page?: number;
  size?: number;
}

class DepositsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /** Agent (or super_admin) proposes a new deposit. The body matches
   *  the backend `DepositProposeBody`; `amount` is a string to avoid
   *  losing precision on large numbers. */
  async propose(payload: DepositProposePayload): Promise<{ request_id: string }> {
    // Idempotency-Key: a double click or a network retry must never propose a
    // second deposit for the same wire — the backend replays the first answer.
    const res = await this.api.post<{ request_id: string }>('/api/v1/deposits', payload, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    });
    return res.data;
  }

  /** Product IDs the client holds an ACTIVE signed contract for — the only
   *  products a deposit can fund. Used to filter the propose-deposit dropdown. */
  async listClientProducts(clientId: string): Promise<string[]> {
    const res = await this.api.get<{ product_ids: string[] }>(
      `/api/v1/deposits/clients/${clientId}/products`,
    );
    return res.data.product_ids;
  }

  /** Paginated listing scoped to the caller's role server-side
   *  (clients see only their own; agents see what they proposed;
   *  managers + super_admin see everything). */
  async list(filters: ListFilters = {}): Promise<DepositListResponse> {
    const res = await this.api.get<DepositListResponse>('/api/v1/deposits', {
      params: filters,
      paramsSerializer: (params) => {
        const parts: string[] = [];
        for (const [key, value] of Object.entries(params)) {
          if (value === undefined || value === null) continue;
          if (Array.isArray(value)) {
            for (const v of value) {
              parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
            }
          } else {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(toText(value))}`);
          }
        }
        return parts.join('&');
      },
    });
    return res.data;
  }

  /** Manager (or super_admin) approves a pending request. The
   *  caller MUST send the `expected_version` they read from the
   *  list response — concurrent approvals fail with 409, and a
   *  stale UI is forced to refresh before retrying. A fresh TOTP
   *  step-up `mfa_code` is required (money-moving action). */
  async approve(
    requestId: string,
    expectedVersion: number,
    mfaCode: string,
  ): Promise<DepositApproveResult> {
    const res = await this.api.post<DepositApproveResult>(
      `/api/v1/deposits/${requestId}/approve`,
      { expected_version: expectedVersion, mfa_code: mfaCode },
    );
    return res.data;
  }

  /** Manager (or super_admin) rejects a pending request. `reason`
   *  is required (5–1000 chars). */
  async reject(
    requestId: string,
    expectedVersion: number,
    reason: string,
  ): Promise<{ request_id: string }> {
    const res = await this.api.post<{ request_id: string }>(
      `/api/v1/deposits/${requestId}/reject`,
      { expected_version: expectedVersion, reason },
    );
    return res.data;
  }

  /** Convenience for the "deposits I'm reviewing" badge — manager-only.
   *  Returns just the count of pending requests, not the full payload. */
  async pendingCount(): Promise<number> {
    const res = await this.list({ status: ['pending'], size: 1 });
    return res.total;
  }

  /** Convenience: round-trip a single request id, used by the staff
   *  detail page when refreshing after an action. */
  async getOne(requestId: string): Promise<DepositRequest | null> {
    const res = await this.list({ size: 100 });  // small size, filter client-side
    return res.items.find((d) => d.id === requestId) ?? null;
  }
}

export default new DepositsService();
